import { useEffect, useRef, useState } from "react";

/**
 * Custom hook that triggers a CSS reveal animation when an element
 * scrolls into the viewport using IntersectionObserver.
 *
 * @param {Object} options
 * @param {number} options.threshold - visibility ratio to trigger (0-1)
 * @param {string} options.rootMargin - margin around root (e.g. "0px 0px -50px 0px")
 * @returns {{ ref, isVisible }}
 */
const useScrollReveal = ({ threshold = 0.1, rootMargin = "0px 0px -60px 0px" } = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element); // only animate once
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return { ref, isVisible };
};

export default useScrollReveal;
